import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Facebook, Check, Download, X, Loader2, AlertCircle, Unplug,
  FolderOpen, ChevronLeft, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function FacebookConnectPage() {
  const [searchParams] = useSearchParams();
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [fbConfigured, setFbConfigured] = useState(false);
  const [isDemo, setIsDemo] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [viewMode, setViewMode] = useState("photos"); // photos | albums
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [nextCursor, setNextCursor] = useState("");

  useEffect(() => {
    checkStatus();
    // Handle OAuth callback code
    const code = searchParams.get("code");
    if (code) {
      exchangeToken(code);
    }
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get("/facebook/status");
      setConnected(res.data.connected);
      setFbConfigured(res.data.fb_configured);
      setIsDemo(res.data.connection?.is_demo !== false);
      if (res.data.connected) loadPhotos();
    } catch { /* ignore */ }
  };

  const exchangeToken = async (code) => {
    setConnecting(true);
    try {
      const redirectUri = window.location.origin + "/facebook";
      await api.post("/facebook/exchange-token", { code, redirect_uri: redirectUri });
      toast.success("Facebook connected successfully!");
      setConnected(true);
      setIsDemo(false);
      // Clean URL
      window.history.replaceState({}, "", "/facebook");
      loadPhotos();
      loadAlbums();
    } catch {
      toast.error("Failed to connect Facebook. Please try again.");
    } finally {
      setConnecting(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // First check if real FB is configured
      const res = await api.get("/facebook/auth-url");
      if (res.data.auth_url) {
        // Real OAuth — redirect to Facebook
        window.location.href = res.data.auth_url;
        return;
      }
      // Demo mode fallback
      await api.post("/facebook/connect");
      setConnected(true);
      setIsDemo(true);
      toast.success("Facebook connected (Demo Mode)");
      loadPhotos();
    } catch {
      toast.error("Failed to connect");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post("/facebook/disconnect");
      setConnected(false);
      setPhotos([]);
      setAlbums([]);
      setActiveAlbum(null);
      toast.success("Facebook disconnected");
    } catch {
      toast.error("Failed to disconnect");
    }
  };

  const loadPhotos = async (cursor = "") => {
    setLoadingPhotos(true);
    try {
      const res = await api.get("/facebook/photos", { params: cursor ? { after: cursor } : {} });
      if (cursor) {
        setPhotos((prev) => [...prev, ...res.data.photos]);
      } else {
        setPhotos(res.data.photos);
      }
      setIsDemo(res.data.is_demo);
      setNextCursor(res.data.next_cursor || "");
    } catch { /* ignore */ }
    finally { setLoadingPhotos(false); }
  };

  const loadAlbums = async () => {
    try {
      const res = await api.get("/facebook/albums");
      setAlbums(res.data.albums || []);
    } catch { /* ignore */ }
  };

  const loadAlbumPhotos = async (album) => {
    setActiveAlbum(album);
    setLoadingPhotos(true);
    try {
      const res = await api.get(`/facebook/albums/${album.id}/photos`);
      setPhotos(res.data.photos);
    } catch {
      toast.error("Failed to load album photos");
    } finally {
      setLoadingPhotos(false);
    }
  };

  const togglePhoto = (url) => {
    const next = new Set(selectedPhotos);
    if (next.has(url)) next.delete(url);
    else next.add(url);
    setSelectedPhotos(next);
  };

  const selectAll = () => {
    const unimported = photos.filter((p) => !p.imported).map((p) => p.url);
    setSelectedPhotos(new Set(unimported));
  };

  const handleImport = async () => {
    if (selectedPhotos.size === 0) {
      toast.error("Select photos to import");
      return;
    }
    setImporting(true);
    try {
      const res = await api.post("/facebook/import", { photo_urls: Array.from(selectedPhotos) });
      toast.success(`${res.data.imported} photos imported to your vault!`);
      setSelectedPhotos(new Set());
      // Reload to update imported status
      if (activeAlbum) {
        loadAlbumPhotos(activeAlbum);
      } else {
        loadPhotos();
      }
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="facebook-connect-page">
      <div className="mb-8">
        <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Integrations</p>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Facebook Connect
        </h1>
        <p className="text-sm text-slate-400 mt-1">Import your photos and memories from Facebook</p>
      </div>

      {/* Mode Notice */}
      {connected && isDemo && (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-amber-500/20 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Demo Mode</p>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              Facebook App credentials are not configured. Sample photos from Unsplash are shown to demonstrate the import workflow.
              To enable real Facebook import, configure your Facebook App ID and Secret in the backend settings.
            </p>
          </div>
        </div>
      )}

      {!fbConfigured && !connected && (
        <div className="bg-white/[0.03] backdrop-blur-xl border border-amber-500/20 rounded-2xl p-5 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-200 font-medium">Facebook App Not Configured</p>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
              To use real Facebook photo import, you need to add your <strong className="text-white">FACEBOOK_APP_ID</strong> and{" "}
              <strong className="text-white">FACEBOOK_APP_SECRET</strong> to the backend environment.
              Until then, you can try the demo mode with sample photos.
            </p>
          </div>
        </div>
      )}

      {connecting && (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-400">Connecting to Facebook...</p>
        </div>
      )}

      {!connected && !connecting ? (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-10 text-center max-w-md mx-auto"
        >
          <div className="w-16 h-16 rounded-2xl bg-blue-600/10 flex items-center justify-center mx-auto mb-6">
            <Facebook className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
            Connect Facebook
          </h2>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">
            {fbConfigured
              ? "Authorize Black Box to access your Facebook photos and import them into your vault."
              : "Import your photos, memories, and posts from Facebook directly into your Black Box vault."
            }
          </p>
          <Button
            onClick={handleConnect}
            disabled={connecting}
            data-testid="facebook-connect-btn"
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-8 py-6 font-medium"
          >
            <Facebook className="w-4 h-4 mr-2" />
            {fbConfigured ? "Connect with Facebook" : "Try Demo Mode"}
          </Button>
          {!fbConfigured && (
            <p className="text-xs text-slate-600 mt-4">Demo uses sample Unsplash photos</p>
          )}
        </motion.div>
      ) : connected && !connecting ? (
        <div>
          {/* Header bar */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              {activeAlbum && (
                <button
                  onClick={() => { setActiveAlbum(null); loadPhotos(); }}
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="fb-back-to-photos"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center">
                <Facebook className="w-4 h-4 text-blue-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">
                  {activeAlbum ? activeAlbum.name : "Facebook Photos"}
                  {isDemo && <span className="text-xs text-amber-400 ml-2">(Demo)</span>}
                </p>
                <p className="text-xs text-slate-500">{photos.length} photos</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {/* View toggle for real mode */}
              {!isDemo && albums.length > 0 && !activeAlbum && (
                <div className="flex bg-white/5 rounded-lg p-0.5">
                  <button
                    onClick={() => setViewMode("photos")}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === "photos" ? "bg-white/10 text-white" : "text-slate-500"
                    }`}
                  >
                    All Photos
                  </button>
                  <button
                    onClick={() => setViewMode("albums")}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === "albums" ? "bg-white/10 text-white" : "text-slate-500"
                    }`}
                  >
                    Albums
                  </button>
                </div>
              )}
              {photos.filter((p) => !p.imported).length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAll}
                  data-testid="facebook-select-all-btn"
                  className="border-white/10 text-slate-400 rounded-full text-xs"
                >
                  Select All ({photos.filter((p) => !p.imported).length})
                </Button>
              )}
              {selectedPhotos.size > 0 && (
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  data-testid="facebook-import-btn"
                  className="bg-[#00FF88] text-black hover:bg-[#00CC6A] rounded-full px-6 font-bold"
                >
                  {importing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Import {selectedPhotos.size} photos
                </Button>
              )}
              <Button
                onClick={handleDisconnect}
                variant="outline"
                data-testid="facebook-disconnect-btn"
                className="border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/30 rounded-full"
              >
                <Unplug className="w-4 h-4 mr-2" /> Disconnect
              </Button>
            </div>
          </div>

          {/* Albums view */}
          {viewMode === "albums" && !activeAlbum && albums.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {albums.map((album) => (
                <motion.div
                  key={album.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => loadAlbumPhotos(album)}
                  className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl overflow-hidden cursor-pointer hover:border-white/15 transition-all group"
                  data-testid={`fb-album-${album.id}`}
                >
                  {album.cover_url ? (
                    <img src={album.cover_url} alt={album.name} className="w-full h-40 object-cover" />
                  ) : (
                    <div className="w-full h-40 bg-white/5 flex items-center justify-center">
                      <FolderOpen className="w-10 h-10 text-slate-700" />
                    </div>
                  )}
                  <div className="p-4">
                    <p className="text-sm font-medium text-white truncate">{album.name}</p>
                    <p className="text-xs text-slate-500 mt-1">{album.count} photos</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Photos grid */}
          {(viewMode === "photos" || activeAlbum) && (
            <>
              {loadingPhotos ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Loading photos...</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {photos.map((photo, i) => {
                      const isSelected = selectedPhotos.has(photo.url);
                      return (
                        <motion.div
                          key={photo.url + i}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.02 }}
                          onClick={() => !photo.imported && togglePhoto(photo.url)}
                          className={`relative rounded-2xl overflow-hidden cursor-pointer group border-2 transition-all duration-200 ${
                            photo.imported
                              ? "border-emerald-500/30 opacity-60 cursor-default"
                              : isSelected
                              ? "border-[#00FF88] shadow-[0_0_15px_rgba(0,255,136,0.2)]"
                              : "border-transparent hover:border-white/20"
                          }`}
                          data-testid={`fb-photo-${i}`}
                        >
                          <img src={photo.url} alt={photo.caption} className="w-full h-48 object-cover" loading="lazy" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <p className="text-xs text-white font-medium truncate">{photo.caption}</p>
                            <p className="text-[10px] text-slate-400">{photo.date}</p>
                          </div>
                          {!photo.imported && (
                            <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                              isSelected ? "bg-[#00FF88] border-[#00FF88]" : "border-white/40 bg-black/30 backdrop-blur-sm"
                            }`}>
                              {isSelected && <Check className="w-3 h-3 text-black" />}
                            </div>
                          )}
                          {photo.imported && (
                            <div className="absolute top-3 right-3 bg-emerald-500 rounded-full px-2 py-0.5 flex items-center gap-1">
                              <Check className="w-3 h-3 text-white" />
                              <span className="text-[10px] text-white font-medium">Imported</span>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                  {/* Load More (real mode with pagination) */}
                  {nextCursor && !activeAlbum && (
                    <div className="text-center mt-8">
                      <Button
                        onClick={() => loadPhotos(nextCursor)}
                        variant="outline"
                        data-testid="fb-load-more"
                        className="border-white/10 text-slate-400 rounded-full"
                      >
                        Load More Photos
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
